import { Badge, Card, CardContent, CardDescription, CardHeader, CardTitle, cn } from '@napaicp/ui'
import type { TFunction } from 'i18next'
import { Link } from 'react-router-dom'
import { competitionStatusLabel } from '@/lib/labels'

/**
 * TASK-U-007 (Wave U Phase 2) — PR-051/UX-046, verifies AC-071.
 *
 * The single Competition card template every list (public, "我的競賽",
 * student dashboard) must render, so the same competition looks the same
 * everywhere: status badge, name, 2–3 line summary, an optional key date,
 * and a CTA — whose text is entirely the caller's decision (BR-097 CTA logic
 * already lives in each page, this component only lays it out consistently).
 *
 * The card's primary navigation target uses the "stretched link" pattern
 * (v3.92, `BR-148`, superseding this file's previous "the whole card is one
 * `<Link>`, no nested anchors" rule): `<Card>` itself is the positioned
 * container, and the primary `<Link>` is absolutely positioned to cover the
 * entire card (`inset-0`, low z-index) rather than wrapping it — so the
 * whole card still navigates on click exactly as before, but a second,
 * independently-clickable element (the optional official-website link,
 * below) can sit on top of it at a higher z-index without being an invalid
 * nested `<a>` inside an `<a>`. `BR-098` is unaffected — this component
 * still never prints `slug` or a bare external URL as text, and the
 * official-website link's visible label is never the raw URL.
 */

const STATUS_BADGE_VARIANT: Record<string, 'default' | 'success' | 'warning' | 'neutral'> = {
  registration_open: 'success',
  submission_open: 'success',
  judging: 'warning',
  completed: 'neutral',
}

export interface CompetitionCardRoleBadge {
  key: string
  label: string
}

/** UX-150: the compact "✓ 報名   ✓ 團隊   ● 作品" row on a Student Journey card — same ✓/●/○ glyph language as the Workspace stepper (UX-082), never a fourth independent vocabulary. */
export interface CompetitionCardProgressItem {
  key: string
  glyph: string
  label: string
}

export interface CompetitionCardProps {
  href: string
  status: string
  name: string
  summary?: string | null
  keyDateLabel?: string
  keyDateValue?: string | null
  ctaLabel: string
  roleBadges?: CompetitionCardRoleBadge[]
  /** PR-188/UX-150: My Competitions' Participant Dashboard cards only — omitted everywhere else (public/admin/judge lists). */
  progress?: CompetitionCardProgressItem[]
  /**
   * Session 10 (v4.45): renders a neutral skeleton in the progress slot
   * while the caller's per-competition progress query is still resolving —
   * ignored once `progress` itself is provided. Never renders a false
   * empty/negative state (no progress row at all) while the real answer is
   * still in flight, per this delivery's established loading-state
   * convention.
   */
  progressLoading?: boolean
  /** AC-373: a short warning line (e.g. "報名資料需補填：團隊聲明") shown above the CTA. Omitted when there is nothing to warn about. */
  notice?: string
  /** v3.92/`BR-148`: renders an independent "官方競賽網站 ↗" CTA when set. `null`/undefined restores the pre-v3.92 single-link card exactly. Never printed as visible text — used purely as the external link's `href`. */
  officialWebsiteUrl?: string | null
  /** Visible label for the official-website link, e.g. `t('competition.officialWebsite')`. Required whenever `officialWebsiteUrl` is set — the caller owns i18n, this component does not. */
  officialWebsiteLabel?: string
  /** SDD §11/§15: an accessible name that names *which* competition's official site this is — the visible label repeats verbatim on every card in a list, so a screen reader stepping through links needs this to tell them apart. Falls back to `officialWebsiteLabel` if omitted. */
  officialWebsiteAriaLabel?: string
  /**
   * PR-268/UX-270/BR-205 (Student Participant UI/UX/IA v4.45): "以其他角色
   * 開啟" — when the caller holds more than one reachable Workspace Role on
   * this one competition, the primary CTA/link above only ever targets one
   * of them (via `homeForCompetition`); these are the *other* reachable
   * workspaces, rendered as independent, lower-visual-weight links (same
   * "second stretched-link on top of the card" pattern as
   * `officialWebsiteUrl`) so a multi-role account never needs two separate
   * Competition Cards to reach both. Omit/empty when there is nothing else
   * to offer — a single-role card never grows this row.
   */
  secondaryWorkspaceLinks?: { key: string; label: string; to: string }[]
  /** Logo Upload SDD §17.4: identity, never navigation — rendered as a plain (non-clickable) thumbnail beside the name. `null`/undefined renders nothing, not a placeholder box (list/card context, unlike the Admin Overview/Drawer's own empty-state box). */
  logoUrl?: string | null
  t: TFunction
  className?: string
}

export function CompetitionCard({
  href,
  status,
  name,
  summary,
  keyDateLabel,
  keyDateValue,
  ctaLabel,
  roleBadges,
  progress,
  progressLoading,
  notice,
  officialWebsiteUrl,
  officialWebsiteLabel,
  officialWebsiteAriaLabel,
  secondaryWorkspaceLinks,
  logoUrl,
  t,
  className,
}: CompetitionCardProps) {
  return (
    <Card
      className={cn(
        'group relative flex h-full flex-col overflow-hidden transition-all duration-150',
        'hover:-translate-y-0.5 hover:border-primary/50 hover:shadow-md',
        className,
      )}
    >
      <Link
        to={href}
        aria-label={name}
        className={cn(
          'absolute inset-0 z-0 cursor-pointer rounded-lg outline-none',
          'focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        )}
      />
      <CardHeader className="flex flex-col gap-2">
        <Badge variant={STATUS_BADGE_VARIANT[status] ?? 'default'}>
          {competitionStatusLabel(t, status)}
        </Badge>
        <div className="flex items-center gap-3">
          {/* Decorative here (empty alt): the name text right next to it is
              already the card's accessible identity (and the stretched
              primary `<Link>` already carries `aria-label={name}`) — a real
              alt would just repeat "X Logo, X" for every screen-reader pass
              over the card. §19 explicitly allows this for a
              same-heading-region decorative logo, applied consistently to
              every card everywhere this component is used. */}
          {logoUrl !== undefined && logoUrl !== null ? (
            <img
              src={logoUrl}
              alt=""
              aria-hidden="true"
              className="size-10 shrink-0 rounded-md object-cover"
            />
          ) : null}
          <CardTitle className="text-xl">{name}</CardTitle>
        </div>
        {roleBadges !== undefined && roleBadges.length > 0 ? (
          <div className="flex flex-wrap gap-1.5">
            {roleBadges.map((badge) => (
              <Badge key={badge.key} variant="neutral">
                {badge.label}
              </Badge>
            ))}
          </div>
        ) : null}
      </CardHeader>
      <CardContent className="flex flex-1 flex-col gap-3">
        {summary !== undefined && summary !== null && summary.trim().length > 0 ? (
          <CardDescription className="line-clamp-3 text-base">{summary}</CardDescription>
        ) : null}
        {progress !== undefined && progress.length > 0 ? (
          <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
            {progress.map((item) => (
              <span key={item.key} className="inline-flex items-center gap-1">
                <span aria-hidden="true">{item.glyph}</span>
                {item.label}
              </span>
            ))}
          </div>
        ) : progressLoading === true ? (
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <span className="h-4 w-32 animate-pulse rounded bg-muted" aria-hidden="true" />
            <span className="sr-only">{t('common:state.loading')}</span>
          </div>
        ) : null}
        {notice !== undefined && notice !== '' ? (
          <p className="text-sm font-medium text-amber-700 dark:text-amber-400" role="status">
            {notice}
          </p>
        ) : null}
        {keyDateValue !== undefined && keyDateValue !== null ? (
          <p className="text-sm text-muted-foreground">
            {keyDateLabel}
            {keyDateLabel !== undefined ? ': ' : ''}
            {keyDateValue}
          </p>
        ) : null}
        <div className="mt-auto flex flex-wrap items-center gap-x-4 gap-y-2">
          <span className="w-fit text-sm font-medium text-primary group-hover:underline">
            {ctaLabel}
          </span>
          {officialWebsiteUrl !== undefined && officialWebsiteUrl !== null ? (
            <a
              href={officialWebsiteUrl}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={officialWebsiteAriaLabel ?? officialWebsiteLabel}
              className={cn(
                'relative z-10 w-fit text-sm font-medium text-muted-foreground underline-offset-2',
                'hover:text-foreground hover:underline',
                'rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
              )}
            >
              {officialWebsiteLabel}
            </a>
          ) : null}
          {secondaryWorkspaceLinks !== undefined && secondaryWorkspaceLinks.length > 0
            ? secondaryWorkspaceLinks.map((link) => (
                <Link
                  key={link.key}
                  to={link.to}
                  className={cn(
                    'relative z-10 w-fit text-sm font-medium text-muted-foreground underline-offset-2',
                    'hover:text-foreground hover:underline',
                    'rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                  )}
                >
                  {link.label}
                </Link>
              ))
            : null}
        </div>
      </CardContent>
    </Card>
  )
}
